import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useTheme } from "@/lib/theme";

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster theme={theme} position="bottom-right" richColors />;
}
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Play from "@/pages/Play";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import OAuthCallback from "@/pages/OAuthCallback";
import GuildSelect from "@/pages/GuildSelect";
import DashboardLayout from "@/pages/DashboardLayout";
import Overview from "@/pages/Overview";
import Engagement from "@/pages/Engagement";
import Moderation from "@/pages/Moderation";
import CustomCommands from "@/pages/CustomCommands";
import Welcome from "@/pages/Welcome";
import Music from "@/pages/Music";
import Announcements from "@/pages/Announcements";
import AiHelp from "@/pages/AiHelp";
import Plugins from "@/pages/Plugins";
import PluginConfig from "@/pages/PluginConfig";
import Analytics from "@/pages/Analytics";
import Logging from "@/pages/Logging";
import Billing from "@/pages/Billing";
import AutoRoles from "@/pages/AutoRoles";
import Social from "@/pages/Social";
import Translation from "@/pages/Translation";
import Timers from "@/pages/Timers";
import Polls from "@/pages/Polls";
import AIStudio from "@/pages/AIStudio";
import Tickets from "@/pages/Tickets";
import Admin from "@/pages/Admin";
import Staff from "@/pages/Staff";
import BotPersonalizer from "@/pages/BotPersonalizer";
import BotMasters from "@/pages/BotMasters";
import Emojis from "@/pages/Emojis";
import ReactionRoles from "@/pages/ReactionRoles";
import StickyMessages from "@/pages/StickyMessages";
import Invites from "@/pages/Invites";import AutoMod from "@/pages/AutoMod";
import Applications from "@/pages/Applications";
import Templates from "@/pages/Templates";
import Birthday from "@/pages/Birthday";
import ProfileCard from "@/pages/ProfileCard";
import Suspended from "@/pages/Suspended";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center bg-black text-zinc-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center bg-black text-zinc-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin" && !user.staff_role) return <Navigate to="/app" replace />;
  return children;
}

function AdminGuard({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "admin" && !user.staff_role) return <Navigate to=".." replace />;
  return children;
}
void AdminGuard;

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ThemedToaster />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/play/:token" element={<Play />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/suspended" element={<Suspended />} />
            <Route path="/oauth/discord/callback" element={<OAuthCallback />} />
            <Route path="/app" element={<Protected><GuildSelect /></Protected>} />
            <Route path="/admin" element={<AdminOnly><Admin /></AdminOnly>} />
            <Route path="/app/g/:guildId" element={<Protected><DashboardLayout /></Protected>}>
              <Route index element={<Overview />} />
              <Route path="engagement" element={<Engagement />} />
              <Route path="leveling" element={<Navigate to="../engagement" replace />} />
              <Route path="leaderboard" element={<Navigate to="../engagement" replace />} />
              <Route path="moderation" element={<Moderation />} />
              <Route path="automod" element={<AutoMod />} />
              <Route path="commands" element={<CustomCommands />} />
              <Route path="autoroles" element={<AutoRoles />} />
              <Route path="welcome" element={<Welcome />} />
              <Route path="economy" element={<Navigate to="../engagement" replace />} />
              <Route path="profile-cards" element={<ProfileCard />} />
              <Route path="birthdays" element={<Birthday />} />
              <Route path="templates" element={<Templates />} />
              <Route path="music" element={<Music />} />
              <Route path="announcements" element={<Announcements />} />
              <Route path="ai-help" element={<AiHelp />} />
              <Route path="social" element={<Social />} />
              <Route path="translation" element={<Translation />} />
              <Route path="timers" element={<Timers />} />
              <Route path="polls" element={<Polls />} />
              <Route path="tickets" element={<Tickets />} />
              <Route path="applications" element={<Applications />} />
              <Route path="staff" element={<Staff />} />
              <Route path="personalizer" element={<BotPersonalizer />} />
              <Route path="botmasters" element={<BotMasters />} />
              <Route path="emojis" element={<Emojis />} />
              <Route path="reaction-roles" element={<ReactionRoles />} />
              <Route path="sticky" element={<StickyMessages />} />
              <Route path="invites" element={<Invites />} />
              <Route path="ai" element={<AIStudio />} />
              <Route path="plugins" element={<Plugins />} />
              <Route path="plugins/:pluginKey" element={<PluginConfig />} />
              <Route path="logging" element={<Logging />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="billing" element={<Billing />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
